import CadastroInterceptor from '@finx/web-repository/src/apis/Cadastro/CadastroInterceptor'
import FactoryResponse from '@/factories/FactoryResponse'
import { toHttpParams, buildQueryParams } from '@/utils/common'
import { ConviteUsuarioDiagnostico, ConviteUsuarioDiagnosticoPayload } from '@/types/interfaces/ConviteUsuarioDiagnostico'
import { CadastroMedico, DataTablePagination, EnvioConvite, UsuarioCadastro, FactoryResponse as ResponseApi, PowerBiEmbeddedInfo, Rede } from '@/types/_'

const factory = new FactoryResponse()
const api = new CadastroInterceptor()

export default {
  async sendCadastroMedico(cadastroMedico: CadastroMedico) {
    const { conviteId } = cadastroMedico
    const request = await api.postAllowAnonymous(`convite-usuarios/${conviteId}/cadastrar-usuario`, cadastroMedico)
    return factory.build(request)
  },

  async deletePessoa(id: string) {
    const request = await api.delete(`pessoas/${id}`)
    return factory.build(request)
  },

  async sendConvite(convite: EnvioConvite) {
    const request = await api.post('convite-usuarios', convite)
    return factory.build(request)
  },

  async deletarConvite(conviteId: string) {
    const request = await api.delete(`convite-usuarios/${conviteId}`)
    return factory.build(request)
  },

  async resendConvite(conviteId: string) {
    const request = await api.post(`convite-usuarios/${conviteId}/reenviar`, conviteId)
    return factory.build(request)
  },

  async getConvitesAll(hospitais: string[]) {
    const params = `hospitais=${hospitais.join('&hospitais=')}`
    const request = await api.get(`convite-usuarios?${params}`)
    return factory.build(request)
  },

  async getConvitesDiagnostico(payload: ConviteUsuarioDiagnosticoPayload): Promise<ResponseApi<ConviteUsuarioDiagnostico[]>> {
    const request = await api.post('convite-usuarios/diagnostico', payload)
    return factory.build<ConviteUsuarioDiagnostico[]>(request)
  },

  async getConviteUsuario(params: any) {
    const request = await api.getAllowAnonymous(`convite-usuarios/${params.conviteId}/tipo-convite/${params.tipoConvite}`)
    return factory.build(request)
  },

  async generateQrCode(conviteId: string) {
    const request = await api.getAllowAnonymous(`convite-usuarios/${conviteId}/qr-assinatura-medico`)
    return factory.build(request)
  },

  async uploadAssinatura(upload: any) {
    const request = await api.postAllowAnonymous(`convite-usuarios/${upload.conviteId}/assinatura`, upload)
    return factory.build(request)
  },

  async getCbosAll() {
    const request = await api.getAllowAnonymous('cbos')
    return factory.build(request)
  },

  async checkExists(cpf: string) {
    const request = await api.getAllowAnonymous(`pessoas/check-cpf-exists/${cpf}`)
    return factory.build(request)
  },

  async validaCpfExists(cpf: string) {
    const request = await api.getAllowAnonymous(`pessoas/check-cpf-exists/${cpf}`)
    return factory.build(request)
  },

  async checkExisteContaCadastrada(cpf: string) {
    const request = await api.getAllowAnonymous(`usuario/check-existe-conta-cadastrada?cpf=${cpf}`)
    return factory.build(request)
  },

  async checkExisteContaCadastradaPorEmail(email: string) {
    const request = await api.getAllowAnonymous(`usuario/any/${email}`)
    return factory.build(request)
  },

  // FXCS-4128 - Diagnostico de Usuario
  async getPessoaByCpf(cpf: string) {
    const request = await api.get(`pessoas/${cpf}`)
    return factory.build(request)
  },

  async getPessoaInfoById(id: string) {
    const request = await api.get(`pessoas/info/${id}`)
    return factory.build(request)
  },

  async pessoaUpsert(usuario: UsuarioCadastro) {
    const request = await api.put('pessoas/upsert', usuario)
    return factory.build(request)
  },

  async getPessoalAll(dataTableItens: any) {
    const { page, rowsPerPage, nome, email, cpf } = dataTableItens

    // Só um de nome/email/cpf chega preenchido: o ConfigUsuarioStore garante um filtro por vez (FXCS-6701).
    const query = buildQueryParams({
      page: page ? String(page) : undefined,
      totalPages: rowsPerPage ? String(rowsPerPage) : undefined,
      nome,
      email,
      cpf,
    })

    const request = await api.get(`pessoas?${query}`)
    return factory.build(request)
  },

  async getPessoalById(id: string) {
    const request = await api.get(`pessoas/info/${id}`)
    return factory.build(request)
  },

  async getPowerBiReportTypes() {
    const response = await api.get('usuarios/current/relatorios')
    return factory.build(response)
  },

  async checkCpfRemuneracao(cpf: string) {
    const request = await api.get(`pessoas/${cpf}?comUsuario=false`)
    return factory.build(request)
  },

  async checkCadastroEmpresaMedica(cnpj: string) {
    const request = await api.get(`empresasmedicas/consulta-por-cnpj/${cnpj}`)
    return factory.build(request)
  },

  async upsertPessoaRemuneracao(pessoa: any) {
    const request = await api.put('pessoas/upsert', pessoa)
    return factory.build(request)
  },

  async getPowerBiEmbeddedInfo(reportId: string, hospitalId?: string, redeId?: string): Promise<ResponseApi<PowerBiEmbeddedInfo>> {
    const queryParams = toHttpParams({ hospitalId: hospitalId || undefined, redeId: redeId || undefined })
    const requestURL = `relatorios/${reportId}/power-bi-embedded${queryParams ? `?${queryParams}` : ''}`

    const request = await api.get(requestURL)
    return factory.build<PowerBiEmbeddedInfo>(request)
  },

  async getRedesCurrentUser(): Promise<ResponseApi<{ redes: Rede[] }>> {
    const request = await api.get('usuarios/current/redes')
    return factory.build<{ redes: Rede[] }>(request)
  },

  async importListaDeSecretariasCsv(hospitalId: string, data: any) { // cargaConviteMedicoUseCaseModel
    const request = await api.upload(`carga-convite-medico/${hospitalId}`, data, { 'Content-Type': 'multipart/form-data' })
    return factory.build(request)
  },

  async upsertEmpresaMedica(params: any) {
    const request = await api.post('empresasmedicas', params)
    return factory.build(request)
  },
}
